import { useMemo, useState, type ReactElement } from "react";
import { ArrowLeft, Camera, ClipboardList, Plus } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, EmptyState, Stack, Text } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { describeApiError } from "../common/errors";
import { AssetForm, type AssetFormWorkspaceContext } from "../assets/AssetForm";
import { AssetPickerDialog } from "../assets/AssetPickerDialog";
import {
    useCreateAssetAssignment,
    useReleaseAssetAssignment,
} from "../asset-assignments/hooks";
import type { AssetAssignmentView } from "../asset-assignments/types";
import type { AssetUpsertPayload, AssetView } from "../assets/types";
import { WorkspaceAssetsPanel } from "./WorkspaceAssetsPanel";
import { useAddAssetToWorkspace, useWorkspaceEntry } from "./hooks";

type InventoryMode =
    | { kind: "browse" }
    | { kind: "add-asset" }
    | { kind: "assign-existing" };

/**
 * Inventory mode is the streamlined, camera-first variant of the
 * workspace-anchored campaign workflow. Same building blocks as the
 * `WorkspacesPage` add-asset path, but stripped to a single workspace and
 * a "snap → save → snap again" loop.
 *
 * What it gives the user that the regular page does not:
 *  - One stable URL they can bookmark or share with a teammate.
 *  - A session counter — visible feedback that yes, the photos they took
 *    actually landed in inventory ("3 added this session"). Critical when
 *    walking with a phone where it's easy to lose track.
 *  - A single sticky "Add asset" CTA in the bottom action bar that opens
 *    the camera straight away — no "find the right button on a crowded
 *    workspace edit page" friction.
 *  - "Done" returns to the workspace edit screen so the user can review
 *    the full result with all the usual context (audit, delete, etc.).
 */
export function InventoryPage(): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const navigate = useNavigate();
    const params = useParams<{ workspaceId: string }>();
    const workspaceId = params.workspaceId ?? "";

    const workspaceQuery = useWorkspaceEntry(workspaceId);
    const workspace = workspaceQuery.data ?? null;

    const [mode, setMode] = useState<InventoryMode>({ kind: "browse" });
    const [pendingRelease, setPendingRelease] = useState<AssetAssignmentView | null>(null);
    // Session counter — every successful add (atomic create, assign of an
    // existing unassigned asset, or re-assign of one that lived
    // elsewhere) bumps this. Resets only on page reload, so a user
    // walking from desk to desk gets per-session-per-workspace stats.
    const [sessionAdds, setSessionAdds] = useState(0);

    const addAssetMutation = useAddAssetToWorkspace();
    const createAssignmentMutation = useCreateAssetAssignment();
    const releaseAssignmentMutation = useReleaseAssetAssignment();

    const workspaceContext: AssetFormWorkspaceContext | undefined = useMemo(() => {
        if (workspace === null) return undefined;
        return {
            id: workspace.id,
            name: workspace.name,
            type: workspace.type,
        };
    }, [workspace]);

    const isWorking =
        addAssetMutation.isPending ||
        createAssignmentMutation.isPending ||
        releaseAssignmentMutation.isPending;

    function done(): void {
        navigate(`/admin/workspaces`);
    }

    function returnToBrowse(): void {
        setMode({ kind: "browse" });
    }

    function bumpAndReturn(): void {
        setSessionAdds((c) => c + 1);
        returnToBrowse();
    }

    function handleAddAssetSave(payload: AssetUpsertPayload): void {
        if (workspace === null) return;
        addAssetMutation.mutate(
            {
                workspaceId: workspace.id,
                payload: {
                    asset: {
                        id: payload.id,
                        sageOfficeId: payload.sageOfficeId,
                        type: payload.type,
                        make: payload.make,
                        model: payload.model,
                        serialNumber: payload.serialNumber,
                        assetTag: payload.assetTag,
                        purchaseDate: payload.purchaseDate,
                        purchasePrice: payload.purchasePrice,
                    },
                    assignmentId: crypto.randomUUID(),
                },
            },
            { onSuccess: bumpAndReturn },
        );
    }

    function handlePickedExistingUnassigned(asset: AssetView): void {
        if (workspace === null) return;
        createAssignmentMutation.mutate(
            {
                id: crypto.randomUUID(),
                assetId: asset.id,
                targetKind: "Workspace",
                workspaceId: workspace.id,
            },
            { onSuccess: bumpAndReturn },
        );
    }

    function handleReassignExistingFromPicker(
        asset: AssetView,
        currentAssignment: AssetAssignmentView,
    ): void {
        if (workspace === null) return;
        reassignAsset(asset.id, currentAssignment.id, workspace.id, bumpAndReturn);
    }

    function handleReassignExistingFromAssetForm(existing: AssetView): void {
        if (workspace === null) return;
        if (
            existing.currentAssignmentId !== null &&
            existing.currentAssignmentId !== undefined &&
            existing.currentAssignmentId !== ""
        ) {
            reassignAsset(existing.id, existing.currentAssignmentId, workspace.id, bumpAndReturn);
        } else {
            createAssignmentMutation.mutate(
                {
                    id: crypto.randomUUID(),
                    assetId: existing.id,
                    targetKind: "Workspace",
                    workspaceId: workspace.id,
                },
                { onSuccess: bumpAndReturn },
            );
        }
    }

    function reassignAsset(
        assetId: string,
        currentAssignmentId: string,
        targetWorkspaceId: string,
        onComplete: () => void,
    ): void {
        // Same release-then-create sequence used in WorkspacesPage. See
        // the comment there for failure-mode rationale; the answer
        // doesn't change here.
        releaseAssignmentMutation.mutate(
            { assetId, assignmentId: currentAssignmentId },
            {
                onSuccess: () => {
                    createAssignmentMutation.mutate(
                        {
                            id: crypto.randomUUID(),
                            assetId,
                            targetKind: "Workspace",
                            workspaceId: targetWorkspaceId,
                        },
                        { onSuccess: onComplete },
                    );
                },
            },
        );
    }

    if (!canEdit) {
        // Inventory is a camera-first capture loop; viewers landing here
        // via a deep link get bounced back to the workspaces list, where
        // the global view-only banner explains the missing role.
        return <Navigate to="/admin/workspaces" replace />;
    }

    if (workspaceQuery.isPending) {
        return (
            <EmptyState
                icon={<ClipboardList size={32} />}
                title="Loading workspace…"
            />
        );
    }

    if (workspace === null) {
        return (
            <EmptyState
                icon={<ClipboardList size={32} />}
                title="Workspace not found"
                description={
                    workspaceQuery.isError
                        ? describeApiError(workspaceQuery.error)
                        : "It may have been deleted. Return to the workspaces list."
                }
                action={
                    <Button variant="primary" onClick={done}>
                        Back to workspaces
                    </Button>
                }
            />
        );
    }

    if (mode.kind === "add-asset") {
        return (
            <AssetForm
                mode={{ kind: "create" }}
                {...(workspaceContext !== undefined ? { workspaceContext } : {})}
                onSave={handleAddAssetSave}
                onCancel={returnToBrowse}
                onRequestDelete={() => undefined}
                onRequestRestore={() => undefined}
                onReassignExisting={handleReassignExistingFromAssetForm}
                isSaving={isWorking}
                errorMessage={
                    addAssetMutation.isError
                        ? describeApiError(addAssetMutation.error)
                        : null
                }
            />
        );
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={<ArrowLeft size={16} />}
                            onClick={done}
                        >
                            Workspaces
                        </Button>
                        <Stack gap={0}>
                            <Text role="heading">Inventory · {workspace.name}</Text>
                            <Text role="muted">
                                {workspace.type} · {workspace.id}
                            </Text>
                        </Stack>
                        <Badge tone="accent">
                            {sessionAdds === 0
                                ? "0 added"
                                : sessionAdds === 1
                                  ? "1 added"
                                  : `${sessionAdds} added`}
                        </Badge>
                    </Stack>
                }
                trailing={
                    !isPhone ? (
                        <Stack direction="row" gap={2}>
                            <Button
                                variant="secondary"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "assign-existing" })}
                            >
                                Assign existing
                            </Button>
                            <Button
                                variant="primary"
                                leadingIcon={<Camera size={16} />}
                                onClick={() => setMode({ kind: "add-asset" })}
                            >
                                Add asset
                            </Button>
                        </Stack>
                    ) : null
                }
            />

            <div
                style={{
                    flex: 1,
                    overflow: "auto",
                    padding: space[4],
                    background: palette.canvas,
                }}
            >
                <Stack gap={4} style={{ maxWidth: 720, margin: "0 auto" }}>
                    <WorkspaceAssetsPanel
                        workspaceId={workspace.id}
                        onAddNewAsset={() => setMode({ kind: "add-asset" })}
                        onAssignExistingAsset={() => setMode({ kind: "assign-existing" })}
                        onReleaseAssignment={(a) => setPendingRelease(a)}
                        disabled={workspace.isDeleted}
                    />
                </Stack>
            </div>

            {isPhone ? (
                <BottomActionBar>
                    <Button
                        variant="secondary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "assign-existing" })}
                    >
                        Existing
                    </Button>
                    <Button
                        variant="primary"
                        leadingIcon={<Camera size={16} />}
                        onClick={() => setMode({ kind: "add-asset" })}
                    >
                        Add asset
                    </Button>
                </BottomActionBar>
            ) : null}

            <ConfirmAction
                open={pendingRelease !== null}
                title="Release asset from this workspace?"
                description="The asset will become unassigned. You can reassign it later from the assets or assignments page."
                confirmLabel="Release"
                busy={releaseAssignmentMutation.isPending}
                onCancel={() => setPendingRelease(null)}
                onConfirm={() => {
                    if (pendingRelease === null) return;
                    const target = pendingRelease;
                    releaseAssignmentMutation.mutate(
                        { assetId: target.assetId, assignmentId: target.id },
                        { onSuccess: () => setPendingRelease(null) },
                    );
                }}
            />

            {mode.kind === "assign-existing" ? (
                <AssetPickerDialog
                    open
                    targetWorkspace={workspace}
                    onAssignUnassigned={handlePickedExistingUnassigned}
                    onReassignAsset={handleReassignExistingFromPicker}
                    onCancel={returnToBrowse}
                    isWorking={
                        createAssignmentMutation.isPending ||
                        releaseAssignmentMutation.isPending
                    }
                />
            ) : null}
        </Stack>
    );
}
